/** DM-Hilfen: Kontakte aus DMs/Präsenzen ableiten, DM öffnen bzw. anlegen. */
import type { ChannelDto, CustomStatusDto, PresenceStatus, UserDto } from '@lagerfeuer/shared';
import { useEffect, useMemo, useState } from 'react';
import { effectiveStatus } from '../../components/Avatar';
import { dmsApi } from '../../lib/endpoints';
import { useAppStore } from '../../store/app';
import { displayName } from '../../store/selectors';

export interface Contact {
  user: UserDto;
  /** Bestehende 1:1-DM mit diesem Nutzer (falls bekannt). */
  dmChannelId: string | null;
  status: PresenceStatus;
  customStatus: CustomStatusDto | null;
}

const STATUS_ORDER: Record<PresenceStatus, number> = { ONLINE: 0, IDLE: 1, DND: 2, INVISIBLE: 3, OFFLINE: 3 };

/**
 * Kontakte = Teilnehmende eigener DMs/Gruppen + Nutzer mit bekannter Präsenz (gemeinsame Server).
 * Eine Freundesliste gibt es im Vertrag nicht.
 */
export function useContacts(): Contact[] {
  const dmIds = useAppStore((s) => s.dmIds);
  const channels = useAppStore((s) => s.channels);
  const users = useAppStore((s) => s.users);
  const presences = useAppStore((s) => s.presences);
  const meId = useAppStore((s) => s.me?.id);
  return useMemo(() => {
    const map = new Map<string, Contact>();
    const add = (u: UserDto, dmChannelId: string | null) => {
      if (u.id === meId || u.isShadow) return;
      const cur = map.get(u.id);
      if (cur) {
        if (!cur.dmChannelId && dmChannelId) cur.dmChannelId = dmChannelId;
        return;
      }
      const p = presences[u.id];
      map.set(u.id, {
        user: users[u.id] ?? u,
        dmChannelId,
        status: effectiveStatus(p?.status),
        customStatus: p?.customStatus ?? null,
      });
    };
    for (const id of dmIds) {
      const ch = channels[id];
      if (!ch) continue;
      for (const u of ch.recipients ?? []) add(u, ch.type === 'DM' ? ch.id : null);
    }
    for (const id of Object.keys(presences)) {
      const u = users[id];
      if (u) add(u, null);
    }
    return [...map.values()].sort(
      (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || displayName(a.user).localeCompare(displayName(b.user), 'de'),
    );
  }, [dmIds, channels, users, presences, meId]);
}

export const isOnline = (c: Pick<Contact, 'status'>) => c.status !== 'OFFLINE' && c.status !== 'INVISIBLE';

export function matchesUser(u: Pick<UserDto, 'username' | 'displayName'>, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return u.username.toLowerCase().includes(n) || (u.displayName ?? '').toLowerCase().includes(n);
}

/** Wert verzögert übernehmen (Suche beim Tippen). */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Bestehende sichtbare 1:1-DM mit einem Nutzer aus dem Store. */
export function findDirectDm(userId: string): ChannelDto | undefined {
  const s = useAppStore.getState();
  for (const id of s.dmIds) {
    const ch = s.channels[id];
    if (ch?.type === 'DM' && (ch.recipients ?? []).some((u) => u.id === userId)) return ch;
  }
  return undefined;
}

/**
 * DM bzw. Gruppen-DM öffnen (Vertrag 3.5 POST /dms; 1 Empfänger idempotent) und in den Store übernehmen.
 */
export async function openDm(recipientIds: string[], name?: string): Promise<ChannelDto> {
  if (recipientIds.length === 1) {
    const existing = findDirectDm(recipientIds[0]!);
    if (existing) return existing;
  }
  const ch = await dmsApi.open(recipientIds, name?.trim() || undefined);
  useAppStore.getState().dispatch('CHANNEL_CREATE', ch);
  return ch;
}

/** DM ausblenden bzw. Gruppe verlassen (DELETE /dms/:id) und lokal entfernen. */
export async function closeDm(channelId: string, leaveGroup: boolean): Promise<void> {
  await dmsApi.close(channelId);
  useAppStore.getState().update((d) => {
    d.dmIds = d.dmIds.filter((x) => x !== channelId);
    if (leaveGroup) {
      delete d.channels[channelId];
      delete d.messages[channelId];
    }
  });
}

/** Freie Plätze in einer (neuen) Gruppe laut `maxGroupDmParticipants` (0 = unbegrenzt). */
export function freeSlots(maxParticipants: number, currentParticipants: number): number {
  if (maxParticipants === 0) return Infinity;
  return Math.max(0, maxParticipants - currentParticipants);
}
